import { CubeIcon } from '@heroicons/react/24/outline'
import type {
  HubRiseCatalog,
  HubRiseOptionList,
  HubRiseProduct,
  HubRiseSku,
  Selection,
} from '../types'

interface Props {
  catalog: HubRiseCatalog
  selection: Selection
  optionListsById: Map<string, HubRiseOptionList>
  onSelectSku: (productId: string, skuId: string) => void
}

const LiveBadge = () => (
  <span className="inline-flex items-center text-[10px] uppercase tracking-wide text-green-700 bg-green-100 rounded px-1.5 py-0.5">
    Live
  </span>
)

const PlatformPriceTable = ({ sku }: { sku: HubRiseSku }) => (
  <div className="border border-gray-200 rounded-md overflow-hidden">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Platform</th>
          <th className="px-3 py-2 text-right font-medium text-gray-600">Price</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Source</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        <tr>
          <td className="px-3 py-2 text-gray-900 font-medium">Base</td>
          <td className="px-3 py-2 text-right text-gray-900">{sku.price}</td>
          <td className="px-3 py-2 text-xs text-gray-500">POS price</td>
        </tr>
        {sku.pricesByPlatform.map((row) => (
          <tr
            key={row.platformRef}
            className={row.isOverride ? 'bg-amber-50 text-amber-900' : ''}
          >
            <td className="px-3 py-2 font-medium">{row.platformName}</td>
            <td className="px-3 py-2 text-right">{row.price}</td>
            <td className="px-3 py-2 text-xs">
              {row.isOverride ? 'Platform markup' : 'Base price'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const TechnicalDetails = ({ rows }: { rows: [string, string][] }) => (
  <details className="mt-4">
    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
      Technical details
    </summary>
    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-xs text-gray-600">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-gray-400">{k}</dt>
          <dd className="break-all">{v}</dd>
        </div>
      ))}
    </dl>
  </details>
)

const ProductView = ({
  product,
  category,
  onSelectSku,
}: {
  product: HubRiseProduct
  category: string
  onSelectSku: (skuId: string) => void
}) => {
  const imageId = product.imageIds?.[0]
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        {imageId ? (
          <img
            src={`https://api.hubrise.com/v1/images/${imageId}`}
            alt={product.name}
            className="w-32 h-32 object-cover rounded-md border border-gray-200"
          />
        ) : (
          <div className="w-32 h-32 bg-gray-100 rounded-md flex items-center justify-center border border-gray-200">
            <CubeIcon className="h-12 w-12 text-gray-400" />
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900">{product.name}</h2>
            <LiveBadge />
          </div>
          <div className="text-sm text-gray-500">{category}</div>
          {product.description && (
            <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
              {product.description}
            </p>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">
          SKUs ({product.skus.length})
        </h3>
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">
                  Base price
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">
                  Platforms
                </th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {product.skus.map((sku) => {
                const hasOverride = sku.pricesByPlatform.some((p) => p.isOverride)
                return (
                  <tr
                    key={sku.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => onSelectSku(sku.id)}
                  >
                    <td className="px-3 py-2 text-gray-900">{sku.name}</td>
                    <td className="px-3 py-2 text-right text-gray-900">{sku.price}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {hasOverride ? (
                        <span className="text-amber-700">Markup applied</span>
                      ) : (
                        'Base only'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <LiveBadge />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <TechnicalDetails
        rows={[
          ['id', product.id],
          ['ref', product.ref],
          ['categoryId', product.categoryId],
        ]}
      />
    </div>
  )
}

const SkuView = ({
  product,
  sku,
  optionListsById,
}: {
  product: HubRiseProduct
  sku: HubRiseSku
  optionListsById: Map<string, HubRiseOptionList>
}) => {
  const linkedLists = (sku.optionListIds ?? [])
    .map((id) => optionListsById.get(id))
    .filter((x): x is HubRiseOptionList => !!x)

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-gray-500">
          {product.name} &nbsp;·&nbsp; SKU
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <h2 className="text-xl font-semibold text-gray-900">{sku.name}</h2>
          <LiveBadge />
        </div>
      </div>

      <PlatformPriceTable sku={sku} />

      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">
          Linked option lists ({linkedLists.length})
        </h3>
        {linkedLists.length === 0 ? (
          <div className="text-sm text-gray-500">None</div>
        ) : (
          <ul className="border border-gray-200 rounded-md divide-y divide-gray-100">
            {linkedLists.map((ol) => (
              <li key={ol.id} className="px-3 py-2 text-sm flex items-center justify-between">
                <span className="text-gray-800">{ol.name}</span>
                <span className="text-xs text-gray-500">
                  {ol.minSelections}–{ol.maxSelections} · {ol.options.length} options
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <TechnicalDetails
        rows={[
          ['id', sku.id],
          ['ref', sku.ref],
          ['basePriceCents', String(sku.basePriceCents)],
        ]}
      />
    </div>
  )
}

const OptionListView = ({ ol }: { ol: HubRiseOptionList }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2">
      <h2 className="text-xl font-semibold text-gray-900">{ol.name}</h2>
      <LiveBadge />
    </div>
    <div className="grid grid-cols-3 gap-3 max-w-md">
      <div className="p-3 bg-gray-50 rounded-md">
        <div className="text-xs text-gray-500">Min</div>
        <div className="text-lg font-semibold text-gray-900">{ol.minSelections}</div>
      </div>
      <div className="p-3 bg-gray-50 rounded-md">
        <div className="text-xs text-gray-500">Max</div>
        <div className="text-lg font-semibold text-gray-900">{ol.maxSelections}</div>
      </div>
      <div className="p-3 bg-gray-50 rounded-md">
        <div className="text-xs text-gray-500">Selection</div>
        <div className="text-sm font-semibold text-gray-900">
          {ol.multipleSelection ? 'Multiple' : 'Single'}
        </div>
      </div>
    </div>

    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-2">
        Options ({ol.options.length})
      </h3>
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ol.options.map((opt) => (
              <tr key={opt.id}>
                <td className="px-3 py-2 text-gray-900">{opt.name}</td>
                <td className="px-3 py-2 text-right text-gray-900">{opt.price}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    <TechnicalDetails
      rows={[
        ['id', ol.id],
        ['ref', ol.ref],
      ]}
    />
  </div>
)

const DetailPane = ({ catalog, selection, optionListsById, onSelectSku }: Props) => {
  if (selection.kind === 'none') {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        Select an item from the left to view details
      </div>
    )
  }

  if (selection.kind === 'optionList') {
    const ol = optionListsById.get(selection.optionListId)
    if (!ol) return <div className="text-sm text-gray-500">Option list not found</div>
    return <OptionListView ol={ol} />
  }

  const product = catalog.products.find((p) => p.id === selection.productId)
  if (!product) return <div className="text-sm text-gray-500">Product not found</div>

  const categoryName =
    catalog.categories.find((c) => c.id === product.categoryId)?.name ?? '—'

  if (selection.kind === 'product') {
    return (
      <ProductView
        product={product}
        category={categoryName}
        onSelectSku={(skuId) => onSelectSku(product.id, skuId)}
      />
    )
  }

  const sku = product.skus.find((s) => s.id === selection.skuId)
  if (!sku) return <div className="text-sm text-gray-500">SKU not found</div>
  return <SkuView product={product} sku={sku} optionListsById={optionListsById} />
}

export default DetailPane
